import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "users/__session/session-context";
import { Masked } from "work/masked-fields";
import type { PolicyReplacementAgreementJSON } from "work/policy-replacements/_schemas/policy-replacement-schema";
import { useGetActiveWorkAgreementsQuery } from "work/work-agreements/_api/work-agreement-api";
import WorkAgreement from "work/work-agreements/work-agreement";

const AdditionRow = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));
const AdditionField = styled(TextField)(() => ({
  flex: "1 1 16rem",
  minWidth: 0,
}));
const Pager = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

/** How many agreements the picker reads at a time. A later page, or a name, reaches the rest. */
export const AGREEMENTS_PER_PAGE = 50;

/** How long the name may rest before the picker asks the server for it. */
export const AGREEMENT_SEARCH_DELAY_MILLISECONDS = 350;

type AgreementChoice = {
  id: string;
  name: string;
  policies: { id: string; name: string }[];
};

type PolicyReplacementAgreementAdditionProps = {
  /** The agreements already in the preview, which the picker leaves out. */
  excludedWorkAgreementIds: ReadonlySet<string>;
  onAdd: (addition: PolicyReplacementAgreementJSON) => void;
};

/**
 * Adds a running agreement to the preview by hand, with the client policy on it that the new
 * document replaces.
 *
 * The search finds agreements by document id, but a vendor roll-out copies a policy into a new
 * document for each proposal, so the same policy can sit under another id. The client names that
 * copy here. Nothing is guessed from file names: replacing the wrong document changes a deal.
 *
 * The server pages and searches the agreements the reader may see, most recently started first, so
 * an older agreement is a name or a page away. Whether an agreement holds a client policy is known
 * only once it is read, so a page can offer fewer agreements than it holds, and the picker says so
 * about that page rather than about every agreement.
 *
 * Native selects rather than MUI menus: a menu is a modal surface that hides the rest of the page
 * while it is open, and this sits in the middle of a form.
 */
export default function PolicyReplacementAgreementAddition(props: Readonly<PolicyReplacementAgreementAdditionProps>) {
  const { excludedWorkAgreementIds, onAdd } = props;
  const session = useSession();
  const [workAgreementId, setWorkAgreementId] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [name, setName] = useState("");
  const [pageIndex, setPageIndex] = useState(0);

  // Memoized on purpose: one debounced function has to survive renders, or each keystroke would
  // start its own timer.
  const commitName = useMemo(
    () =>
      debounce((value: string) => {
        setName(value.trim());
        setPageIndex(0);
        setWorkAgreementId("");
        setDocumentId("");
      }, AGREEMENT_SEARCH_DELAY_MILLISECONDS),
    [],
  );
  useEffect(() => () => commitName.cancel(), [commitName]);

  const agreementsQuery = useGetActiveWorkAgreementsQuery({
    session,
    pageSize: AGREEMENTS_PER_PAGE,
    pageIndex,
    name: name || undefined,
  });
  const choices = useMemo<AgreementChoice[]>(
    () =>
      (agreementsQuery.data?.data ?? []).flatMap((json) => {
        const agreement = WorkAgreement.fromJSON(json);
        const id = agreement.id?.value;
        const policies = agreement.clientPolicies;
        if (!id || !agreement.isActiveWork || Masked.isMasked(policies) || policies.length === 0) return [];
        return [
          {
            id,
            name: agreement.name?.value ?? "Unnamed agreement",
            policies: policies.map((policy) => ({ id: policy.id.value, name: policy.name?.value ?? "Unnamed document" })),
          },
        ];
      }),
    [agreementsQuery.data],
  );
  const offered = choices.filter((choice) => !excludedWorkAgreementIds.has(choice.id));
  const chosen = offered.find((choice) => choice.id === workAgreementId);
  const page = agreementsQuery.data
    ? PaginatedResponse.from(agreementsQuery.data.data, agreementsQuery.data.headers)
    : undefined;
  const hasOtherPages = page !== undefined && (page.hasPreviousPage || page.hasNextPage);

  function turnPage(nextPageIndex: number) {
    setPageIndex(nextPageIndex);
    setWorkAgreementId("");
    setDocumentId("");
  }

  // Reached only through Add, which is enabled only once both are chosen.
  function handleAdd(agreement: AgreementChoice) {
    onAdd({ workAgreementId: agreement.id, replacedDocumentId: documentId });
    setWorkAgreementId("");
    setDocumentId("");
  }

  return (
    <>
      <AdditionRow>
        <AdditionField
          size="small"
          type="search"
          label="Find an agreement by name"
          value={nameDraft}
          onChange={(event) => {
            setNameDraft(event.target.value);
            commitName(event.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </AdditionRow>
      <AdditionRow>
        <AdditionField
          select
          size="small"
          label="Agreement"
          value={workAgreementId}
          disabled={agreementsQuery.isFetching || offered.length === 0}
          onChange={(event) => {
            setWorkAgreementId(event.target.value);
            setDocumentId("");
          }}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
        >
          <option value="">Choose an agreement</option>
          {offered.map((choice) => (
            <option key={choice.id} value={choice.id}>
              {choice.name}
            </option>
          ))}
        </AdditionField>
        <AdditionField
          select
          size="small"
          label="Client policy the new document replaces"
          value={documentId}
          disabled={!chosen}
          onChange={(event) => setDocumentId(event.target.value)}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
        >
          <option value="">Choose a policy</option>
          {chosen?.policies.map((policy) => (
            <option key={policy.id} value={policy.id}>
              {policy.name}
            </option>
          ))}
        </AdditionField>
        <Button variant="outlined" startIcon={<AddIcon />} disabled={!chosen || !documentId} onClick={() => handleAdd(chosen!)}>
          Add agreement
        </Button>
      </AdditionRow>
      {/* Gated on the first load, not on every fetch. A work-agreement Ably event refetches this
          list while the form is open; unmounting the line for the refetch moved everything below
          it, the page's submit button included, by the line's height mid-click. The select above
          is disabled while fetching, so the last answer showing for a moment misleads nobody. */}
      {page && !agreementsQuery.isLoading && offered.length === 0 && (
        <Typography variant="body2" color="text.secondary" role="status" sx={{ marginTop: 1 }}>
          {describeNothingOffered(page.totalElements, hasOtherPages, name)}
        </Typography>
      )}
      {page && hasOtherPages && (
        <Pager>
          <Button size="small" disabled={!page.hasPreviousPage || agreementsQuery.isFetching} onClick={() => turnPage(pageIndex - 1)}>
            Previous agreements
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {pageIndex + 1} of {page.totalPages}
          </Typography>
          <Button size="small" disabled={!page.hasNextPage || agreementsQuery.isFetching} onClick={() => turnPage(pageIndex + 1)}>
            More agreements
          </Button>
        </Pager>
      )}
    </>
  );
}

/**
 * Why the picker offers nothing, claiming only what it read: no agreement at all, or none on this
 * page or among those found that holds a client policy the preview does not already have.
 */
export function describeNothingOffered(totalElements: number, hasOtherPages: boolean, name: string): string {
  if (totalElements === 0) {
    return name ? `No agreement you can see has "${name}" in its name.` : "You have no running agreements to add.";
  }
  if (hasOtherPages) return "No running agreement on this page holds a client policy that is not already in the preview.";
  return name
    ? `No running agreement with "${name}" in its name holds a client policy that is not already in the preview.`
    : "No running agreement you can see holds a client policy that is not already in the preview.";
}
